import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/store/modules/user';
import { asyncRoutes } from './routes';
import { filterRoutesByPermission, extractPermissionKeys } from '@sys/shared/permission';

// 静态路由（无 permission 字段视为公开页面）
const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { hidden: true },
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/layout/DefaultLayout.vue'),
    redirect: '/dashboard',
    children: [],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: { hidden: true },
  },
];

const router = createRouter({
  history: createWebHistory('/brand-admin/'),
  routes: constantRoutes,
});

// 标记是否已挂载动态路由
let dynamicRoutesAdded = false;

/**
 * 加载并挂载动态路由
 * @param {string[]} permissions 用户权限列表
 */
function loadDynamicRoutes(permissions) {
  const layout = router.getRoutes().find(r => r.name === 'Layout');
  if (!layout) return;

  // 根据权限过滤路由
  const filtered = filterRoutesByPermission(asyncRoutes, permissions);

  // 清空之前添加的子路由（热更新场景）
  layout.children
    ?.filter(child => child.name !== 'Dashboard')
    ?.forEach(child => {
      router.removeRoute(child.name);
    });

  filtered.forEach(route => {
    router.addRoute('Layout', route);
  });

  dynamicRoutesAdded = true;
}

/**
 * 重置动态路由（用于登出或切换账号）
 */
export function resetRouter() {
  const layout = router.getRoutes().find(r => r.name === 'Layout');
  if (!layout) return;

  layout.children?.forEach(child => {
    router.removeRoute(child.name);
  });

  dynamicRoutesAdded = false;
}

// ==================== 路由守卫 ====================
// 规则：无 permission 字段的路由直接放行；有 permission 则匹配用户权限 key
// 预留区域：可根据业务需要手动补充守卫规则

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();

  // 1. 已登录但未挂载动态路由 -> 先挂载，再重新导航
  // 必须放在最前面，否则 redirect 到子路由时会因路由不存在而匹配 catch-all 404
  if (userStore.isLoggedIn && !dynamicRoutesAdded) {
    const permissions = userStore.permissions?.length
      ? userStore.permissions
      : extractAllPermissions();
    loadDynamicRoutes(permissions);
    return next({ path: to.path, query: to.query, hash: to.hash, replace: true });
  }

  // 2. 未登录 -> 只有 Login 页能访问，其余全部重定向到登录
  // 命中 NotFound 时不保存重定向路径，避免登录后跳转到不存在的页面
  if (!userStore.isLoggedIn) {
    if (to.name === 'Login') {
      return next();
    }
    if (to.name === 'NotFound') {
      return next({ name: 'Login' });
    }
    return next({ name: 'Login', query: { redirect: to.fullPath } });
  }

  // 3. 已登录访问登录页 -> 跳转首页
  if (to.name === 'Login') {
    return next('/');
  }

  // 4. 无 permission 标识的路由视为公开页面，直接放行
  if (!to.meta?.permission) {
    return next();
  }

  // 5. 权限 key 匹配校验
  if (!userStore.hasPermission(to.meta.permission)) {
    return next({ name: 'NotFound' });
  }

  // 6. 自定义守卫规则预留区（手动填写）
  // --------------------------------------------------
  // 示例：
  // if (to.meta?.needBrandNo && !userStore.brandNo) {
  //   return next('/select-brand')
  // }
  // --------------------------------------------------

  next();
});

router.afterEach(to => {
  document.title = to.meta?.title ? `${to.meta.title} - 品牌后台` : '品牌后台';
});

/**
 * 提取本地所有权限 key（仅本地开发兜底用）
 */
function extractAllPermissions() {
  return extractPermissionKeys(asyncRoutes);
}

export default router;
